import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartColumn,
  faFilePdf,
  faFilter,
  faRotate,
  faSearch, faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import Breadcrumb from "../../../../components/Ui_Element/Breadcrumb";
import { showToast } from "../../../../components/Ui_Element/CustomToast";
import config from "../../../../config";
import { getlist } from "../../../../contexts/apicontexts";
import { getCurrentUserIdentity } from "../../ticketUtils";
import {
  fetchTicketsSummaryGrouped,
  fetchTicketReportStatusOptions,
} from "./ticketsSummaryReportService";
import { openTicketsSummaryPdfReport } from "./ticketsSummaryPdfService";

const toDateInputValue = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  const offsetMs = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offsetMs).toISOString().split("T")[0];
};

const getDefaultStartDate = () => {
  const date = new Date();
  date.setDate(1);
  return toDateInputValue(date);
};

const getDefaultEndDate = () => {
  const date = new Date();
  date.setMonth(date.getMonth() + 1, 0);
  return toDateInputValue(date);
};

const normalizeOrganization = (item) => ({
  id: Number(item?.id ?? item?.Id ?? 0),
  name: String(item?.name ?? item?.Name ?? "").trim(),
  organizationCode: String(item?.organizationCode ?? item?.OrganizationCode ?? "").trim(),
  address: String(item?.address ?? item?.Address ?? "").trim(),
  mobile: String(item?.mobile ?? item?.Mobile ?? "").trim(),
  primaryEmail: String(item?.primaryEmail ?? item?.PrimaryEmail ?? "").trim(),
  website: String(item?.website ?? item?.Website ?? "").trim(),
});

const normalizeCustomer = (item) => ({
  id: Number(item?.id ?? item?.Id ?? 0),
  name: String(item?.name ?? item?.Name ?? "").trim(),
  customerCode: String(item?.customerCode ?? item?.CustomerCode ?? "").trim(),
  organizationId: Number(item?.organizationId ?? item?.OrganizationId ?? 0),
  organizationName: String(item?.organizationName ?? item?.OrganizationName ?? "").trim(),
  email: String(item?.email ?? item?.Email ?? "").trim(),
  mobile: String(item?.mobile ?? item?.Mobile ?? "").trim(),
  address: String(item?.address ?? item?.Address ?? "").trim(),
  domain: String(item?.domain ?? item?.Domain ?? "").trim(),
  empUserId: String(item?.empUserId ?? item?.EmpUserId ?? "").trim(),
});

const normalizeCustomerUser = (item) => ({
  id: Number(item?.id ?? item?.Id ?? 0),
  customerId: Number(item?.customerId ?? item?.CustomerId ?? 0),
  userId: String(item?.userId ?? item?.UserId ?? "").trim(),
  fullName: String(item?.fullName ?? item?.FullName ?? item?.name ?? item?.Name ?? "").trim(),
  email: String(item?.email ?? item?.Email ?? "").trim(),
  customerName: String(item?.customerName ?? item?.CustomerName ?? "").trim(),
  organizationName: String(item?.organizationName ?? item?.OrganizationName ?? "").trim(),
});

const normalizeEmployee = (item) => ({
  id: Number(item?.id ?? item?.Id ?? 0),
  userId: String(item?.userId ?? item?.UserId ?? item?.userID ?? item?.UserID ?? "").trim(),
  fullName: String(item?.fullName ?? item?.FullName ?? item?.name ?? item?.Name ?? "").trim(),
  employeeCode: String(item?.employeeCode ?? item?.EmployeeCode ?? "").trim(),
  employeeNumber: String(item?.employeeNumber ?? item?.EmployeeNumber ?? "").trim(),
  workEmail: String(item?.workEmail ?? item?.WorkEmail ?? item?.email ?? item?.Email ?? "").trim(),
});

const emptyReport = {
  items: [],
  totalTickets: 0,
};

function ReportField({ label, required = false, children, helper }) {
  return (
    <div>
      <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
        {label} {required ? <span className="text-rose-500">*</span> : null}
      </label>
      {children}
      {helper ? <div className="mt-2 text-xs text-slate-500">{helper}</div> : null}
    </div>
  );
}

export default function TicketsSummaryReportPage() {
  const identity = useMemo(() => getCurrentUserIdentity(), []);
  const [organizations, setOrganizations] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerUsers, setCustomerUsers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [statusOptions, setStatusOptions] = useState(["All"]);
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState("");
  const [report, setReport] = useState(emptyReport);
  const [filters, setFilters] = useState({
    organizationId: "",
    employeeUserId: "",
    customerId: "",
    customerUserId: "",
    startDate: getDefaultStartDate(),
    endDate: getDefaultEndDate(),
    status: "All",
  });

  useEffect(() => {
    let cancelled = false;

    const loadFilters = async () => {
      setFiltersLoading(true);
      setError("");

      try {
        const [organizationResponse, customerResponse, customerUserResponse, employeeResponse, statusResponse] = await Promise.all([
          getlist(`${config.baseurl}Organizations/getlist`),
          getlist(`${config.baseurl}Customer/getlist`),
          getlist(`${config.baseurl}CustomerUser/getlist`),
          getlist(`${config.baseurl}Employee/getlist`),
          fetchTicketReportStatusOptions(),
        ]);

        if (cancelled) return;

        setOrganizations(Array.isArray(organizationResponse?.data) ? organizationResponse.data.map(normalizeOrganization) : []);
        setCustomers(Array.isArray(customerResponse?.data) ? customerResponse.data.map(normalizeCustomer) : []);
        setCustomerUsers(Array.isArray(customerUserResponse?.data) ? customerUserResponse.data.map(normalizeCustomerUser) : []);
        setEmployees(Array.isArray(employeeResponse?.data) ? employeeResponse.data.map(normalizeEmployee) : []);
        setStatusOptions(["All", ...statusResponse]);
      } catch {
        if (!cancelled) {
          setError("Report filters could not be loaded.");
        }
      } finally {
        if (!cancelled) setFiltersLoading(false);
      }
    };

    loadFilters();

    return () => {
      cancelled = true;
    };
  }, []);

  const selectedOrganization = useMemo(
    () => organizations.find((item) => Number(item.id) === Number(filters.organizationId || 0)) || null,
    [filters.organizationId, organizations]
  );

  const visibleCustomers = useMemo(() => {
    if (!filters.organizationId) return customers;
    return customers.filter((item) => Number(item.organizationId || 0) === Number(filters.organizationId));
  }, [customers, filters.organizationId]);

  const selectedEmployee = useMemo(
    () => employees.find((item) => item.userId === filters.employeeUserId) || null,
    [employees, filters.employeeUserId]
  );

  const visibleCustomersByEmployee = useMemo(() => {
    if (!filters.employeeUserId) return visibleCustomers;
    return visibleCustomers.filter((item) => String(item.empUserId || "").trim() === filters.employeeUserId);
  }, [filters.employeeUserId, visibleCustomers]);

  const visibleCustomerIdSet = useMemo(
    () => new Set(visibleCustomersByEmployee.map((item) => Number(item.id))),
    [visibleCustomersByEmployee]
  );

  const visibleCustomerUsers = useMemo(() => {
    if (filters.customerId) {
      return customerUsers.filter((item) => Number(item.customerId || 0) === Number(filters.customerId));
    }

    if (filters.organizationId || filters.employeeUserId) {
      return customerUsers.filter((item) => visibleCustomerIdSet.has(Number(item.customerId || 0)));
    }

    return customerUsers;
  }, [customerUsers, filters.customerId, filters.employeeUserId, filters.organizationId, visibleCustomerIdSet]);

  const selectedCustomer = useMemo(
    () => customers.find((item) => Number(item.id) === Number(filters.customerId || 0)) || null,
    [customers, filters.customerId]
  );

  const selectedCustomerUser = useMemo(
    () => customerUsers.find((item) => item.userId === filters.customerUserId) || null,
    [customerUsers, filters.customerUserId]
  );

  const reportCompanyInfo = useMemo(() => {
    if (selectedOrganization) {
      return {
        name: selectedOrganization.name || "BanglaFire CRM",
        code: selectedOrganization.organizationCode || "",
        email: selectedOrganization.primaryEmail || selectedCustomer?.email || "support@banglafire.com",
        phone: selectedOrganization.mobile || selectedCustomer?.mobile || "",
        website: selectedOrganization.website || selectedCustomer?.domain || "www.banglafire.com",
        address: selectedOrganization.address || selectedCustomer?.address || "BanglaFire CRM Support Desk",
      };
    }

    if (selectedCustomer) {
      return {
        name: selectedCustomer.name || "BanglaFire CRM",
        code: selectedCustomer.customerCode || "",
        email: selectedCustomer.email || "support@banglafire.com",
        phone: selectedCustomer.mobile || "",
        website: selectedCustomer.domain || "www.banglafire.com",
        address: selectedCustomer.address || "BanglaFire CRM Support Desk",
      };
    }

    return {
      name: "BanglaFire CRM",
      code: "Support Desk",
      email: "support@banglafire.com",
      phone: "",
      website: "www.banglafire.com",
      address: "BanglaFire CRM Support Desk",
    };
  }, [selectedCustomer, selectedOrganization]);

  const reportFilterSummary = useMemo(() => ({
    organizationName: selectedOrganization?.name || "All Organizations",
    employeeName: selectedEmployee?.fullName || selectedEmployee?.workEmail || "All Employees",
    customerName: selectedCustomer?.name || "All Customers",
    customerCode: selectedCustomer?.customerCode || "",
    customerUserName: selectedCustomerUser?.fullName || selectedCustomerUser?.email || "All Customer Users",
    customerUserEmail: selectedCustomerUser?.email || "",
    startDate: filters.startDate,
    endDate: filters.endDate,
    status: filters.status === "All" ? "All Statuses" : filters.status,
  }), [filters.endDate, filters.startDate, filters.status, selectedCustomer, selectedCustomerUser, selectedEmployee, selectedOrganization]);

  useEffect(() => {
    if ((filters.organizationId || filters.employeeUserId) && !visibleCustomersByEmployee.some((item) => Number(item.id) === Number(filters.customerId || 0))) {
      setFilters((current) => ({ ...current, customerId: "", customerUserId: "" }));
      return;
    }

    if (filters.customerUserId && !visibleCustomerUsers.some((item) => item.userId === filters.customerUserId)) {
      setFilters((current) => ({ ...current, customerUserId: "" }));
    }
  }, [filters.organizationId, filters.employeeUserId, filters.customerId, filters.customerUserId, visibleCustomersByEmployee, visibleCustomerUsers]);

  const handleFilterChange = (field, value) => {
    setFilters((current) => {
      if (field === "organizationId") {
        return {
          ...current,
          organizationId: value,
          employeeUserId: "",
          customerId: "",
          customerUserId: "",
        };
      }

      if (field === "employeeUserId") {
        return {
          ...current,
          employeeUserId: value,
          customerId: "",
          customerUserId: "",
        };
      }

      if (field === "customerId") {
        return {
          ...current,
          customerId: value,
          customerUserId: "",
        };
      }

      return {
        ...current,
        [field]: value,
      };
    });
  };

  const validateFilters = () => {
    if (!filters.startDate || !filters.endDate) {
      throw new Error("Start date and end date are required.");
    }

    if (filters.endDate < filters.startDate) {
      throw new Error("End date must be on or after start date.");
    }
  };

  const handleSearch = async () => {
    try {
      validateFilters();
    } catch (validationError) {
      const message = validationError?.message || "Please review the report filters.";
      setError(message);
      showToast.error(message);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const result = await fetchTicketsSummaryGrouped({
        organizationId: filters.organizationId,
        employeeUserId: filters.employeeUserId,
        customerId: filters.customerId,
        customerUserId: filters.customerUserId,
        startDate: filters.startDate,
        endDate: filters.endDate,
        status: filters.status === "All" ? "" : filters.status,
      });

      setReport({ items: result.items || [], totalTickets: Number(result.totalTickets || 0) });
      setSearched(true);
    } catch (requestError) {
      const message = requestError?.message || "Tickets summary report could not be loaded.";
      setError(message);
      setReport(emptyReport);
      setSearched(true);
      showToast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleExportPdf = async () => {
    try {
      await openTicketsSummaryPdfReport({
        companyInfo: reportCompanyInfo,
        filters: reportFilterSummary,
        report,
      });
    } catch (exportError) {
      const message = exportError?.message || "PDF report could not be generated.";
      showToast.error(message);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6">
      <Breadcrumb pageName="Tickets Summary Report" currentPage="Tickets Summary Report" />

      <div className="mt-4 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="relative overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(249,115,22,0.18),_transparent_30%),linear-gradient(135deg,#0f172a,#1e293b_52%,#334155)] px-5 py-6 text-white md:px-7 md:py-8">
          <div className="absolute right-0 top-0 h-40 w-40 translate-x-8 -translate-y-8 rounded-full bg-white/10 blur-3xl" />
          <div className="relative flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-slate-100">
                <FontAwesomeIcon icon={faChartColumn} />
                Support Reporting
              </div>
              <h1 className="text-2xl font-semibold tracking-tight md:text-3xl">Organization, employee, customer user ticket summary</h1>

              <div className="mt-4 flex flex-wrap gap-3 text-xs text-slate-200">
                <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1">{identity.fullName || identity.email || "Current user"}</span>
                {selectedOrganization ? (
                  <span className="rounded-full border border-amber-300/30 bg-amber-400/15 px-3 py-1 text-amber-50">
                    Organization: {selectedOrganization.name}
                  </span>
                ) : null}
                {filters.status !== "All" ? (
                  <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1">Status: {filters.status}</span>
                ) : null}
              </div>
            </div>

            <button
              type="button"
              onClick={handleSearch}
              disabled={loading || filtersLoading}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-amber-400 px-5 py-3 text-sm font-semibold text-slate-950 shadow-lg transition hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-70"
            >
              <FontAwesomeIcon icon={loading ? faSpinner : faSearch} spin={loading} />
              {loading ? "Loading Report..." : "Run Report"}
            </button>
          </div>
        </div>

        <div className="space-y-6 p-5 md:p-6">
          <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Filters</h2>

              </div>
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-900 text-white">
                <FontAwesomeIcon icon={faFilter} />
              </div>
            </div>

            {error ? <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div> : null}
            <div className="mt-5 grid gap-4 lg:grid-cols-3">
              <ReportField label="Start Date" required>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(event) => handleFilterChange("startDate", event.target.value)}
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-slate-400"
                />
              </ReportField>

              <ReportField label="End Date" required>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(event) => handleFilterChange("endDate", event.target.value)}
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-slate-400"
                />
              </ReportField>

              <ReportField label="Organization">
                <select
                  value={filters.organizationId}
                  onChange={(event) => handleFilterChange("organizationId", event.target.value)}
                  disabled={filtersLoading}
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-slate-400 disabled:bg-slate-50"
                >
                  <option value="">All Organizations</option>
                  {organizations.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </ReportField>

              <ReportField label="Employee">
                <select
                  value={filters.employeeUserId}
                  onChange={(event) => handleFilterChange("employeeUserId", event.target.value)}
                  disabled={filtersLoading}
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-slate-400 disabled:bg-slate-50"
                >
                  <option value="">All Employees</option>
                  {employees.map((item) => (
                    <option key={item.userId || item.id} value={item.userId}>
                      {item.fullName || item.workEmail || item.userId}
                      {item.employeeCode ? ` (${item.employeeCode})` : item.employeeNumber ? ` (${item.employeeNumber})` : ""}
                    </option>
                  ))}
                </select>
              </ReportField>

              <ReportField
                label="Customer"

              >
                <select
                  value={filters.customerId}
                  onChange={(event) => handleFilterChange("customerId", event.target.value)}
                  disabled={filtersLoading}
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-slate-400 disabled:bg-slate-50"
                >
                  <option value="">{filters.organizationId || filters.employeeUserId ? "All Customers" : "Select Customer"}</option>
                  {visibleCustomersByEmployee.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}{item.customerCode ? ` (${item.customerCode})` : ""}
                    </option>
                  ))}
                </select>
              </ReportField>

              <ReportField label="Customer User">
                <select
                  value={filters.customerUserId}
                  onChange={(event) => handleFilterChange("customerUserId", event.target.value)}
                  disabled={filtersLoading}
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-slate-400 disabled:bg-slate-50"
                >
                  <option value="">All Customer Users</option>
                  {visibleCustomerUsers.map((item) => (
                    <option key={`${item.id}-${item.userId}`} value={item.userId}>
                      {item.fullName || item.email || item.userId}
                      {item.customerName ? ` - ${item.customerName}` : ""}
                    </option>
                  ))}
                </select>
              </ReportField>



              <ReportField label="Status">
                <select
                  value={filters.status}
                  onChange={(event) => handleFilterChange("status", event.target.value)}
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-slate-400"
                >
                  {statusOptions.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </ReportField>

              <button
                type="button"
                onClick={() => {
                  setFilters({
                    organizationId: "",
                    employeeUserId: "",
                    customerId: "",
                    customerUserId: "",
                    startDate: getDefaultStartDate(),
                    endDate: getDefaultEndDate(),
                    status: "All",
                  });
                  setReport(emptyReport);
                  setError("");
                  setSearched(false);
                }}
                className="rounded-full border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Reset
              </button>
              <button
                type="button"
                onClick={handleSearch}
                disabled={loading || filtersLoading}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 px-2 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70"
              >
                <FontAwesomeIcon icon={loading ? faSpinner : faSearch} spin={loading} />
                Search
              </button>
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Status Snapshot</h2>
                <p className="mt-1 text-sm text-slate-500">Quick ticket count by status based on the current report result.</p>
              </div>
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={handleSearch}
                  disabled={loading || filtersLoading}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  <FontAwesomeIcon icon={loading ? faSpinner : faRotate} spin={loading} />
                  Refresh Result
                </button>
                <button
                  type="button"
                  onClick={handleExportPdf}
                  disabled={loading || filtersLoading}
                  className="inline-flex items-center gap-2 rounded-full bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  <FontAwesomeIcon icon={faFilePdf} />
                  PDF Report
                </button>
              </div>
            </div>

            <div className="mt-5 flex flex-wrap gap-3">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Total Tickets</div>
                <div className="mt-1 text-2xl font-semibold text-slate-900">{report.totalTickets || 0}</div>
              </div>
            </div>
          </div>

          <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 px-5 py-4">
              <h2 className="text-lg font-semibold text-slate-900">Tickets Summary Rows</h2>

            </div>

            {loading ? (
              <div className="px-6 py-16 text-center text-sm text-slate-500">
                <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                Loading report rows...
              </div>
            ) : !searched ? (
              <div className="px-6 py-16 text-center text-sm text-slate-500">
                Run the report to see grouped ticket summary rows.
              </div>
            ) : report.items.length ? (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 text-left">
                    <tr>
                      <th className="px-5 py-3 font-semibold text-slate-600">Organization</th>
                      <th className="px-5 py-3 font-semibold text-slate-600">Employee</th>
                      <th className="px-5 py-3 font-semibold text-slate-600">Customer</th>
                      <th className="px-5 py-3 font-semibold text-slate-600">Customer User</th>
                      <th className="px-5 py-3 text-right font-semibold text-slate-600">Tickets Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.items.map((item, index) => (
                      <tr key={`${item.organizationName}-${item.employeeName}-${item.customerName}-${item.customerUserName}-${index}`} className="border-t border-slate-100">
                        <td className="px-5 py-3 text-slate-700">{item.organizationName}</td>
                        <td className="px-5 py-3 text-slate-700">{item.employeeName}</td>
                        <td className="px-5 py-3 font-medium text-slate-900">{item.customerName}</td>
                        <td className="px-5 py-3 text-slate-700">{item.customerUserName}</td>
                        <td className="px-5 py-3 text-right font-semibold text-slate-900">{item.ticketsCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="px-6 py-16 text-center text-sm text-slate-500">
                No tickets matched the selected filters.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
